import {
  type Range,
  getMarkRange,
  getMarkType,
  getNodeType,
  isNodeActive,
  posToDOMRect,
} from '@tiptap/core'
import type { MarkType, NodeType } from '@tiptap/pm/model'
import {
  Plugin,
  PluginKey,
  type EditorState,
  type PluginView,
  type Selection,
} from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import type { MutableRefObject } from 'react'
import { Instance as TippyInstance } from 'tippy.js'
import type {
  HyperlinkManagerPluginProps,
  HyperlinkManagerViewProps,
} from './types'

type LinkState = {
  /**
   * True if the cursor is in a link, or the current selection
   * contains a link.
   */
  cursorInLink: boolean
  /**
   * The full selection range of the currently-selected link.
   */
  linkRange?: Range
}

/**
 * Manages the popover's open state when the editor
 * is interacted with.
 */
class HyperlinkManagerView implements PluginView {
  /**
   * The current ProseMirror editor view. This should
   * be received from the Plugin constructor's `view`
   * callback.
   */
  public view: EditorView
  /**
   * A reference to the popover's content element.
   */
  public popoverEl: HTMLElement
  /**
   * The `MarkType` for the link schema.
   */
  public linkType: MarkType

  /**
   * The `NodeType` for Opal's embeddedResource extension
   */
  public embeddedResourceType: NodeType
  /**
   * A React ref handle that holds the current open state,
   * as managed in the TextEditor context.
   */
  public externalOpenState: MutableRefObject<boolean>
  /**
   * The selection state when the popover was opened.
   */
  public initialSelection?: Selection
  /**
   * A callback to get the current Tippy instance. Since
   * Tippy is managed by the HyperlinkManager component,
   * we can't assume the instance always exists.
   */
  public getTippy: () => TippyInstance | null

  constructor({
    view,
    element,
    getTippy,
    externalOpenState = { current: false },
  }: HyperlinkManagerViewProps) {
    this.view = view
    this.popoverEl = element
    this.getTippy = getTippy
    this.externalOpenState = externalOpenState
    this.linkType = getMarkType('link', view.state.schema)
    this.embeddedResourceType = getNodeType(
      'embeddedResource',
      view.state.schema
    )

    this.view.dom.addEventListener('blur', this.blurHandler)
    this.view.dom.addEventListener('dragstart', this.dragstartHandler)
    // Detaches menu content from its current parent
    this.popoverEl.remove()
  }

  get tippy() {
    return this.getTippy()
  }

  dragstartHandler = () => this.hide()

  blurHandler = (e: FocusEvent) => {
    const isActionViewPopover =
      e.relatedTarget instanceof Element &&
      this.popoverEl.contains(e.relatedTarget)

    // Hide if editor was blurred & popover is not receiving focus
    if (!e.relatedTarget || !isActionViewPopover) {
      this.hide()
    }
  }

  /**
   * Gets selection metadata related to the currently-selected link, if any.
   */
  getLinkRange({ selection }: EditorState): LinkState {
    const { $from, from, to } = selection
    const linkRange = getMarkRange($from, this.linkType) || undefined
    const cursorInLink =
      !!linkRange &&
      // Ignore if length of selection is 0 & cursor is at the end of the link
      !(linkRange.to === to && from === to) &&
      // Ensure selection doesn't exceed the bounds of the link
      linkRange.from <= from &&
      linkRange.to >= to

    return { cursorInLink, linkRange }
  }

  isEmbedActive(state: EditorState): boolean {
    return isNodeActive(state, this.embeddedResourceType)
  }

  update(view: EditorView, oldState?: EditorState) {
    if (!view.hasFocus()) {
      return
    }

    const selection = view.state.selection
    const { from, to } = selection

    const initialSelectionChanged =
      this.initialSelection && !this.initialSelection.eq(selection)
    const { cursorInLink, linkRange } = this.getLinkRange(view.state)
    const { cursorInLink: prevCursorInLink = false } =
      (oldState && this.getLinkRange(oldState)) ?? {}

    // Hide popover if: current selection is not in a link, and one of these:
    // 1: Previous selection was in a link
    // 2: Selection has changed since popover was initialized
    const shouldHide =
      !cursorInLink && (prevCursorInLink || initialSelectionChanged)

    // Show popover if:
    // 1: It was externally opened (e.g. when creating a link), or
    // 2: Current selection is a link, and it wasn't during last update, or
    //    (we want to avoid updating Tippy if the cursor state didn't change)
    // 3: the above is true, but the popover isn't open
    const shouldShow =
      this.externalOpenState.current ||
      this.isEmbedActive(view.state) ||
      (cursorInLink &&
        (!prevCursorInLink || this.tippy?.state.isShown === false))

    if (shouldHide) {
      this.hide()
      this.initialSelection = undefined
    } else if (shouldShow) {
      // Adjust reference rect only if position would change
      if (initialSelectionChanged !== false) {
        this.tippy?.setProps({
          getReferenceClientRect: () =>
            // Align with link if possible, otherwise align with selection
            posToDOMRect(view, linkRange?.from ?? from, linkRange?.to ?? to),
        })
      }

      this.show()
      this.initialSelection = selection
    }
  }

  show() {
    this.tippy?.show()
  }

  hide() {
    this.tippy?.hide()
  }

  destroy() {
    this.view.dom.removeEventListener('dragstart', this.dragstartHandler)
    this.view.dom.removeEventListener('blur', this.blurHandler)
  }
}

export const HyperlinkManagerPlugin = (
  pluginKey: PluginKey | string,
  options: HyperlinkManagerPluginProps
) => {
  return new Plugin({
    key: typeof pluginKey === 'string' ? new PluginKey(pluginKey) : pluginKey,
    view: (view) => new HyperlinkManagerView({ view, ...options }),
  })
}
